import React, { useCallback, useEffect, useState } from 'react'
import styles from '../../styles/front/common/FloatingPromotionBanner.module.sass'

// gtag用の拡張型定義
declare global {
  interface Window {
    gtag: (event: string, action: string, params: {}) => void
  }
}

type Props = {
  imageUrl: string
  originalImageUrl: string
  targetUrl: string
  imageAspect: 'square' | 'portrait_4_5'
  triggerSeconds: number
  triggerScrollRate: number
  scrollTarget: string
}

// ポップアップ非表示期間（日数）
const POPUP_DISPLAY_INTERVAL_DAY = 7
const SESSION_STORAGE_KEY = 'floatingPromotionBannerClosed'
const LOCAL_STORAGE_KEY = 'floatingPromotionBannerLastShown'

// 表示可能かチェック
const isDisplayable = (): boolean => {
  // セッション内で閉じた場合は表示しない
  if (sessionStorage.getItem(SESSION_STORAGE_KEY)) {
    return false
  }

  // 7日以内に表示した場合は表示しない
  const lastShown = localStorage.getItem(LOCAL_STORAGE_KEY)
  if (lastShown) {
    const lastShownDate = new Date(lastShown)
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - lastShownDate.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays < POPUP_DISPLAY_INTERVAL_DAY) {
      return false
    }
  }

  return true
}

const FloatingPromotionBanner = ({
  imageUrl,
  originalImageUrl,
  targetUrl,
  imageAspect,
  triggerSeconds,
  triggerScrollRate,
  scrollTarget,
}: Props) => {
  const [isVisible, setIsVisible] = useState(false)
  const [timeTriggered, setTimeTriggered] = useState(false)
  const [scrollTriggered, setScrollTriggered] = useState(false)
  const [canDisplay] = useState(() => isDisplayable())

  // Google Analyticsのイベントを送信
  const gtagEvent = useCallback((eventName: string) => {
    if (window.gtag) {
      window.gtag('event', eventName, {})
    }
  }, [])

  // バナーを表示
  const showBanner = useCallback(() => {
    if (!canDisplay) return

    setIsVisible(true)
    gtagEvent('imp_floating_banner')
  }, [canDisplay, gtagEvent])

  // バナーを閉じる
  const closeBanner = useCallback(() => {
    setIsVisible(false)
    // ×ボタンを押した時にlocalStorageとsessionStorageの両方に記録
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true')
    localStorage.setItem(LOCAL_STORAGE_KEY, new Date().toISOString())
    gtagEvent('close_floating_banner')
  }, [gtagEvent])

  // リンククリック時のイベント
  const handleLinkClick = useCallback(() => {
    gtagEvent('click_floating_banner')
  }, [gtagEvent])

  // スクロール監視
  const handleScroll = useCallback(() => {
    if (scrollTriggered) return

    let scrollPercent = 0

    if (scrollTarget === 'page') {
      // ページ全体のスクロール率
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight
      scrollPercent = scrollHeight === 0 ? 100 : (scrollTop / scrollHeight) * 100
    } else {
      // メインコンテンツ領域のスクロール率
      const targetElement = document.querySelector(scrollTarget) as HTMLElement
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect()
        const elementTop = rect.top + window.pageYOffset
        const elementHeight = targetElement.offsetHeight
        const windowHeight = window.innerHeight
        const scrollTop = window.pageYOffset

        // 要素の上端が画面内に入ったかどうか
        if (scrollTop + windowHeight > elementTop) {
          // 要素内をどれだけスクロールしたか（0〜100%）
          const scrolledInElement = scrollTop + windowHeight - elementTop
          const maxScrollInElement = elementHeight + windowHeight
          scrollPercent = (scrolledInElement / maxScrollInElement) * 100
        }
      } else {
        // セレクタが見つからない場合はページ全体で判定
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight
        scrollPercent = scrollHeight === 0 ? 100 : (scrollTop / scrollHeight) * 100
      }
    }

    if (scrollPercent >= triggerScrollRate) {
      setScrollTriggered(true)
    }
  }, [scrollTriggered, scrollTarget, triggerScrollRate])

  // 両方のトリガーが満たされたかチェックして表示
  useEffect(() => {
    if (timeTriggered && scrollTriggered) {
      showBanner()
    }
  }, [timeTriggered, scrollTriggered, showBanner])

  // タイマー設定
  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeTriggered(true)
    }, triggerSeconds * 1000)

    return () => clearTimeout(timer)
  }, [triggerSeconds])

  // スクロールイベント登録（throttled with requestAnimationFrame）
  useEffect(() => {
    let ticking = false
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener('scroll', throttledScroll)
    return () => window.removeEventListener('scroll', throttledScroll)
  }, [handleScroll])

  if (!isVisible) return null

  return (
    <div className={styles.floatingPromotionBanner}>
      <button
        className={styles.closeButton}
        type="button"
        aria-label="閉じる"
        onClick={closeBanner}
      >
        ×
      </button>
      <a
        className={styles.link}
        href={targetUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleLinkClick}
      >
        <img
          className={`${styles.image} ${imageAspect === 'square' ? styles.square : imageAspect === 'portrait_4_5' ? styles.portrait : ''}`}
          src={imageUrl}
          onError={(e) => {
            e.currentTarget.onerror = null
            e.currentTarget.src = originalImageUrl
          }}
          alt="フローティングバナー"
        />
      </a>
    </div>
  )
}

export default FloatingPromotionBanner
